import {
  UIEntitiesRef,
  updateAllEntities,
  updateEntities,
  upsertEntities,
  upsertEntitiesById
} from '@ngneat/elf-entities';
import {AxiosError, AxiosResponse} from 'axios';
import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';

import APIAxios, {APIRoutes} from '@api/axios.api';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {Pagination} from '@store/common/pagination.model';
import {
  ExecutionLine,
  ExecutionLinePagination,
  ExecutionLinesFilters,
  ExecutionLinesGroupedAction,
  ExecutionLineStatus,
  ExecutionLineTotalDto,
  ExecutionLineUpdateDto
} from '@store/invoices/executionLines/executionLine.model';
import {
  getExecutionLinesDataSource,
  getExecutionLinesTotalDataSource,
} from '@store/invoices/executionLines/executionLine.requests';
import {executionLineStore} from '@store/invoices/executionLines/executionLine.store';
import SnackError from '@utils/error.utils';
import {executionLineQuery} from '@store/invoices/executionLines/executionLine.query';
import {GridSortItem} from '@mui/x-data-grid-pro';
import {TableFilter} from '@store/common/tableFilter.model';
import {readyForInvoicingKey} from '@screens/admin/invoices/executions/Executions.tab';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';

export class ExecutionLineService {
  store = executionLineStore;

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: {
      ids: [],
      state: state.filters.state,
      search: state.filters.search,
      tableFilters: ExecutionLinesFilters.initTableFilters(),
      excludeTableFilters: ExecutionLinesFilters.initExcludeTableFilters(),
      filterInvoicingRequired: state.filters.filterInvoicingRequired
    },
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getExecutionLines = (): Observable<ExecutionLinePagination> => {
    const {
      filters: {
        from: fromDate,
        to: toDate,
        status,
        search,
        tableFilters,
        excludeTableFilters,
        filterReadyForInvoicing,
        invoicingSource,
        state,
        ids,
        serviceValidationStatus,
      },
      sort,
      tablePagination
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);
    const requestExcludeFilters = TableFilter.toRequestFilters(excludeTableFilters);
    const readyForInvoicingIds = filterReadyForInvoicing ? executionLineQuery.getExecutionLinesReadyForInvoicingIds() : [];

    return from(APIAxios({
      ...APIRoutes.GETExecutionLines(),
      params: {
        ...requestFilters,
        ...requestExcludeFilters,
        searchString: search || undefined,
        from: fromDate?.toISOString() || undefined,
        to: toDate?.toISOString() || undefined,
        state: state,
        status,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        ids: readyForInvoicingIds && readyForInvoicingIds.length > 0 ? readyForInvoicingIds.join(',') : ids?.join(','),
        invoicingSource,
        serviceValidationStatus: serviceValidationStatus || undefined,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ExecutionLinePagination>) => response.data),
        tap((executionLinesPagination) => {
          const paginationData = Pagination.toPaginationData(executionLinesPagination, tablePagination.page);

          this.store.update(
            upsertEntities(executionLinesPagination.data),
            upsertEntitiesById(executionLinesPagination.data.map((execLine) => {
              return {id: execLine.id, executionId: execLine.executionId};
            }).map((e) => e.id), {
              updater: (execLineUi) => execLineUi,
              creator: (id: any) => {
                const find = executionLinesPagination.data.find((e) => e.id === id);
                return {
                  id: id,
                  executionId: find?.executionId ?? 0,
                  selected: false,
                };
              },
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, executionLinesPagination.data.map((executionLine) => executionLine.id)),
            getExecutionLinesDataSource.setSuccess(),
          );
        }),
        getExecutionLinesDataSource.trackRequestStatus(),
      );
  };

  getExecutionLinesTotal = (ids: number[]): Observable<ExecutionLineTotalDto> => {
    return from(APIAxios({
      ...APIRoutes.GETExecutionLinesTotalAmount(),
      params: {
        ids: ids.join(',')
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ExecutionLineTotalDto>) => response.data),
        tap((resp) => {
          this.store.update(
            (state) => ({
              ...state,
              total: resp,
            }),
            getExecutionLinesTotalDataSource.setSuccess(),
          );
        }),
        getExecutionLinesTotalDataSource.trackRequestStatus(),
      );
  };

  setSelectedExecutionLines = (executionLineIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (executionLineUI) => ({
          ...executionLineUI,
          selected: executionLineIds.includes(executionLineUI.id)
        }),
        {ref: UIEntitiesRef},
      ),
    );
  };

  patchExecutionLine = (executionLineId: number, updatedExecutionLine: Partial<ExecutionLine>): Observable<any> => {
    const data: ExecutionLineUpdateDto = {
      unitCostInCents: updatedExecutionLine.puBuy,
      unitPriceInCents: updatedExecutionLine.puSell,
      quantity: updatedExecutionLine.quantity,
      status: updatedExecutionLine.executionLineStatus,
      supplierInvoicingRequired: updatedExecutionLine.supplierInvoicingRequired,
      clientInvoicingRequired: updatedExecutionLine.clientInvoicingRequired,
      supplierInvoicingDelayed: updatedExecutionLine.supplierInvoicingDelayed,
      clientInvoicingDelayed: updatedExecutionLine.clientInvoicingDelayed,
    };
    return from(APIAxios({
      ...APIRoutes.PATCHExecutionLineId(executionLineId),
      data
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ExecutionLine>) => response.data),
        tap((resp) => {
          this.store.update(
            updateEntities(executionLineId, () => resp),
          );
        }),
      );
  };

  updateExecutionLinesDelayed = (data: {
    updateStatus?: ExecutionLineStatus;
    supplierInvoicingDelayed?: boolean;
    clientInvoicingDelayed?: boolean;
  }) => {
    const {
      filters: {from: fromDate, to: toDate, filterInvoicingRequired, status, search, tableFilters}
    } = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.PATCHExecutionLinesStatus(),
      data: {
        ...requestFilters,
        searchString: search || undefined,
        from: fromDate?.toISOString() || undefined,
        to: toDate?.toISOString() || undefined,
        filterInvoicingRequired,
        status,
        supplierInvoicingDelayed: data.supplierInvoicingDelayed,
        clientInvoicingDelayed: data.clientInvoicingDelayed,
        updateStatus: data.updateStatus,
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getExecutionLines()),
      );
  };

  executionLinesGroupedAction = (groupedAction: ExecutionLinesGroupedAction, executionLineIds?: number[]): Observable<number[]> => {
    const ids = executionLineIds ?? executionLineQuery.selectedExecutionLine;

    return from(APIAxios({
      ...APIRoutes.PATCHExecutionLinesGroupedAction(),
      data: {ids, ...groupedAction},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getExecutionLines().pipe(map(() => ids))),
        tap(() => this.setSelectedExecutionLines([])),
      );
  };


  deleteGroupedExecutionLines() {
    const ids = executionLineQuery.selectedExecutionLineByExecutionId;
    return from(
      APIAxios({
        ...APIRoutes.DELETEExecutions(),
        data: {ids: ids.map((e) => e.executionId)},
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      switchMap(() => this.getExecutionLines().pipe(map(() => ids))),
      tap(() => this.setSelectedExecutionLines([])),
    );
  }

  clearExecutionLinesReadyForInvoicing() {
    localStorage.setItem(readyForInvoicingKey, JSON.stringify([]));
  }

  removeExecutionLinesFromReadyForInvoicing(execLineIds: number[]) {
    let readyForInvoicing = executionLineQuery.getExecutionLinesReadyForInvoicing();

    readyForInvoicing = readyForInvoicing.filter((execLine) => !execLineIds.includes(execLine.id));

    localStorage.setItem(readyForInvoicingKey, JSON.stringify(readyForInvoicing));
  }

  getExecutionLinesCSV() {
    const {
      filters: {
        from: fromDate,
        to: toDate,
        status,
        search,
        state,
        tableFilters,
        excludeTableFilters,
        filterReadyForInvoicing,
        invoicingSource,
      },
      sort,
      tablePagination
    } = this.store.getValue();

    console.log(this.store.getValue());
    const requestFilters = TableFilter.toRequestFilters(tableFilters);
    const requestExcludeFilters = TableFilter.toRequestFilters(excludeTableFilters);
    const readyForInvoicingIds = filterReadyForInvoicing ? executionLineQuery.getExecutionLinesReadyForInvoicingIds() : [];

    console.log(requestFilters);

    return from(APIAxios({
      ...APIRoutes.GETExecutionLinesCSV(),
      params: {
        ...requestFilters,
        ...requestExcludeFilters,
        searchString: search || undefined,
        from: fromDate?.toISOString() || undefined,
        to: toDate?.toISOString() || undefined,
        state,
        status,
        disablePaginate: true,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
        ids: readyForInvoicingIds.join(','),
        invoicingSource,
      },
    }))
      .pipe(
        catchError(async (err: AxiosError) => {
          const errText = JSON.parse(await (err.response as any)?.data?.text());
          throw new SnackError(errText.message, 'error');
        }),
        tap((response: AxiosResponse<Blob>) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'execution-lines.xlsx');
        }),
      );
  }

  fixPrices(serviceId: number, body: {
    sellPrice: number | undefined;
    buyPrice: number | undefined;
    from: Date;
    to: Date;
  }) {
    return from(APIAxios({
      ...APIRoutes.PATCHFixPrices(serviceId), data: {
        from: body.from,
        to: body.to,
        unitPriceInCents: body.sellPrice != undefined ? body.sellPrice * 100 : undefined,
        unitCostInCents: body.buyPrice != undefined ? body.buyPrice * 100 : undefined,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<{ updatedCount: number }>) => {
          return response.data;
        }));
  }
}

export const executionLineService = new ExecutionLineService();
